import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp } from "lucide-react";
import GlassSheet from "@/components/v2/GlassSheet";
import KrishnaAvatar from "@/components/KrishnaAvatar";
import UserAvatar from "@/components/UserAvatar";
import { api } from "@/hooks/useAuth";

/**
 * BirthIntakeChat — Krishna asks for birth details and 4+ life events natively via chat.
 *
 * Steps:
 *  dob -> time -> time_source -> city -> country -> event1..event4 -> confirm -> compute
 *
 * Geocoding: we use the free Open-Meteo geocoding endpoint (no API key).
 */

const STEPS = [
  { id: "dob", prompt: "First, when were you born? Please tell me the date — year, month, day.", placeholder: "e.g. 10 August 1988", parse: parseDOB },
  { id: "time", prompt: "What time of day did you arrive? Even a close minute matters here.", placeholder: "e.g. 06:57 AM", parse: parseTime },
  { id: "time_source", prompt: "How do you know this time? From a birth certificate, your family patri, or memory?", placeholder: "certificate / patri / memory", parse: parseTimeSource },
  { id: "city", prompt: "In which city were you born?", placeholder: "e.g. Mumbai", parse: (v) => (v.trim() ? { ok: true, value: v.trim() } : { ok: false, error: "A city name helps me find the stars overhead at that moment." }) },
  { id: "country", prompt: "And which country?", placeholder: "e.g. India", parse: (v) => (v.trim() ? { ok: true, value: v.trim() } : { ok: false, error: "Please share the country." }) },
  { id: "event1", prompt: "Now share four moments from your life so I can see your rhythm. First — any education, degree, or major study, with the year.", placeholder: "e.g. Finished engineering in 2010", parse: parseEvent },
  { id: "event2", prompt: "Second — any marriage, serious relationship, or long partnership, with the year.", placeholder: "e.g. Married in 2017 — or none", parse: parseEvent },
  { id: "event3", prompt: "Third — a career turn: a new job, business, or quit, with the year.", placeholder: "e.g. Started my company in 2019", parse: parseEvent },
  { id: "event4", prompt: "Fourth — a relocation, move, or major transition, with the year.", placeholder: "e.g. Moved abroad in 2012", parse: parseEvent },
];

function parseDOB(raw) {
  const v = raw.trim();
  // ISO
  let m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return { ok: true, value: `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` };
  // DD/MM/YYYY or DD-MM-YYYY
  m = v.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
  if (m) return { ok: true, value: `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` };
  // "10 Aug 1988" / "10 August 1988"
  const MONTHS = { jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };
  m = v.match(/^(\d{1,2})\s+([a-zA-Z]+)\s+(\d{4})$/);
  if (m && MONTHS[m[2].slice(0,3).toLowerCase()]) {
    const mo = MONTHS[m[2].slice(0,3).toLowerCase()];
    return { ok: true, value: `${m[3]}-${String(mo).padStart(2,'0')}-${m[1].padStart(2,'0')}` };
  }
  return { ok: false, error: "I could not read that date. Try: 10 Aug 1988 or 1988-08-10." };
}

function parseTime(raw) {
  const v = raw.trim();
  let m = v.match(/^(\d{1,2}):(\d{2})\s*([ap]\.?m\.?)?$/i);
  if (!m) return { ok: false, error: "Please write time as HH:MM, with AM/PM if applicable." };
  let h = parseInt(m[1], 10), min = parseInt(m[2], 10);
  if (m[3]) {
    const ampm = m[3].replace(/\./g, "").toLowerCase();
    if (ampm === "pm" && h < 12) h += 12;
    if (ampm === "am" && h === 12) h = 0;
  }
  if (h < 0 || h > 23 || min < 0 || min > 59) return { ok: false, error: "That hour or minute looks off." };
  return { ok: true, value: `${String(h).padStart(2,'0')}:${String(min).padStart(2,'0')}` };
}

function parseTimeSource(raw) {
  const v = raw.trim().toLowerCase();
  if (!v) return { ok: false, error: "Please tell me the source." };
  if (/(cert|hosp)/.test(v)) return { ok: true, value: "certificate" };
  if (/(patr|janma|janam)/.test(v)) return { ok: true, value: "patri" };
  if (/(mem|told|parent|mother|family|appro)/.test(v)) return { ok: true, value: "memory" };
  if (/exact/.test(v)) return { ok: true, value: "exact" };
  return { ok: true, value: "memory" };
}

function parseEvent(raw) {
  const v = raw.trim();
  if (!v) return { ok: false, error: "Even one line is enough — add the year." };
  if (/^(none|skip|no)$/i.test(v)) return { ok: true, value: null };
  const m = v.match(/(\d{4})/);
  if (!m) return { ok: false, error: "Please include the year (e.g. ‘Moved abroad in 2012’)." };
  const year = parseInt(m[1], 10);
  if (year < 1900 || year > 2100) return { ok: false, error: "That year is out of range." };
  const mm = v.match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s*\d{0,4}/i);
  const MONTHS = { jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };
  const month = mm ? MONTHS[mm[1].toLowerCase()] : null;
  return { ok: true, value: { description: v, year, month } };
}

async function geocodeCity(city, country) {
  try {
    const q = encodeURIComponent(`${city}${country ? ", " + country : ""}`);
    const r = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${q}&count=1&language=en&format=json`);
    const d = await r.json();
    const hit = d.results?.[0];
    if (!hit) return null;
    const tzOffsetHours = typeof hit.timezone_offset === "number" ? hit.timezone_offset / 3600 : null;
    return { latitude: hit.latitude, longitude: hit.longitude, timezone: tzOffsetHours, tz_name: hit.timezone };
  } catch (e) { return null; }
}

async function tzFromName(tzName) {
  // Fallback: estimate offset in hours from the IANA name using Intl
  try {
    const d = new Date();
    const dtf = new Intl.DateTimeFormat("en-US", { timeZone: tzName, timeZoneName: "shortOffset" });
    const parts = dtf.formatToParts(d);
    const off = parts.find((p) => p.type === "timeZoneName")?.value || "";
    const m = off.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
    if (m) {
      const sign = m[1].startsWith("-") ? -1 : 1;
      const h = Math.abs(parseInt(m[1], 10));
      const mm = m[2] ? parseInt(m[2], 10) : 0;
      return sign * (h + mm / 60);
    }
  } catch (e) {}
  return null;
}

export default function BirthIntakeChat({ open, onClose, user, onComplete }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [stepIdx, setStepIdx] = useState(0);
  const [draft, setDraft] = useState({ events: [] });
  const [working, setWorking] = useState(false);
  const scrollRef = useRef(null);

  const step = STEPS[stepIdx];
  const done = stepIdx >= STEPS.length;

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        { role: "ai", text: "I want to understand the timing of your life — the stars overhead when you arrived. May I ask you a few things?" },
        { role: "ai", text: STEPS[0].prompt },
      ]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const pushAI = (text) => setMessages((m) => [...m, { role: "ai", text }]);
  const pushUser = (text) => setMessages((m) => [...m, { role: "user", text }]);

  const compute = async (finalDraft) => {
    setWorking(true);
    pushAI("Thank you. Let me look at the sky at that moment…");
    try {
      // 1) Save birth profile with geocoded lat/lon/tz
      let geo = await geocodeCity(finalDraft.city, finalDraft.country);
      let tz = geo?.timezone;
      if (geo && tz == null && geo.tz_name) tz = await tzFromName(geo.tz_name);
      if (!geo || tz == null) {
        pushAI("I could not pinpoint your city on the map. Please double-check the spelling and try again.");
        setWorking(false);
        return;
      }
      await api.put("/profile/birth", {
        date_of_birth: finalDraft.dob,
        time_of_birth: finalDraft.time,
        time_source: finalDraft.time_source,
        birth_city: finalDraft.city,
        birth_country: finalDraft.country,
        latitude: geo.latitude,
        longitude: geo.longitude,
        timezone: tz,
      });
      // 2) Save life events
      for (const ev of finalDraft.events) {
        if (!ev) continue;
        await api.post("/profile/life-events", {
          description: ev.description, year: ev.year, month: ev.month,
          category: "intake",
        });
      }
      // 3) Compute chart (deducts credits)
      const { data } = await api.post("/vedic/compute-chart", { force: false });
      const chart = data.chart;
      const md = chart?.dashas?.vimshottari?.find((x) => true);
      pushAI(
        `Your Lagna rises in ${chart?.lagna?.sign} (${chart?.lagna?.pos}), Moon in ${chart?.planets?.Moon?.nakshatra}. ` +
        `The timing engine is now active — I can speak of your Future, Why, and Remedies.`
      );
      toast.success("Chart computed.");
      onComplete?.(chart);
      setTimeout(() => onClose(), 900);
    } catch (e) {
      if (e.response?.status === 402) pushAI("Your prasad bowl is empty. Earn or top up credits before I compute the chart.");
      else pushAI("Something delayed the computation. Please try again in a moment.");
      toast.error(e.response?.data?.detail || "Chart computation failed.");
    } finally {
      setWorking(false);
    }
  };

  const submit = async () => {
    const raw = input.trim();
    if (!raw || working) return;
    setInput("");
    pushUser(raw);

    if (done) return;
    const parsed = step.parse(raw);
    if (!parsed.ok) {
      pushAI(parsed.error);
      return;
    }

    // persist to draft
    const next = { ...draft };
    if (step.id === "dob") next.dob = parsed.value;
    else if (step.id === "time") next.time = parsed.value;
    else if (step.id === "time_source") next.time_source = parsed.value;
    else if (step.id === "city") next.city = parsed.value;
    else if (step.id === "country") next.country = parsed.value;
    else if (step.id.startsWith("event")) next.events = [...(next.events || []), parsed.value];
    setDraft(next);

    const nextIdx = stepIdx + 1;
    setStepIdx(nextIdx);
    if (nextIdx < STEPS.length) {
      setTimeout(() => pushAI(STEPS[nextIdx].prompt), 180);
    } else {
      setTimeout(() => compute(next), 300);
    }
  };

  const skip = () => {
    if (step?.id?.startsWith("event")) {
      pushUser("skip");
      const next = { ...draft, events: [...(draft.events || []), null] };
      setDraft(next);
      const nextIdx = stepIdx + 1;
      setStepIdx(nextIdx);
      if (nextIdx < STEPS.length) setTimeout(() => pushAI(STEPS[nextIdx].prompt), 180);
      else setTimeout(() => compute(next), 300);
    }
  };

  const authGate = !user;

  return (
    <GlassSheet open={open} onClose={onClose} eyebrow="Share your stars" title="KALKI is still mapping the stars" testid="intake-sheet" side="bottom">
      {authGate ? (
        <p className="text-slate-300">Please sign in first so I can remember your chart across every conversation.</p>
      ) : (
        <>
          <div ref={scrollRef} className="max-h-[48vh] overflow-y-auto kalki-scroll pr-2 flex flex-col gap-4 mb-5">
            {messages.map((m, i) => (
              m.role === "user" ? (
                <div key={i} className="flex items-start gap-3 justify-end" data-testid="intake-msg-user">
                  <div className="rounded-3xl rounded-tr-sm text-slate-100 text-[14.5px] leading-relaxed px-5 py-3 border border-amber-200/30"
                       style={{ background: "linear-gradient(135deg,rgba(60,80,180,0.5), rgba(140,80,200,0.35))" }}>
                    {m.text}
                  </div>
                  <UserAvatar size={32} initial={(user?.name || "You").charAt(0).toUpperCase()} />
                </div>
              ) : (
                <div key={i} className="flex items-start gap-3" data-testid="intake-msg-ai">
                  <KrishnaAvatar size={36} />
                  <div className="rounded-3xl rounded-tl-sm bg-white/5 border border-white/10 px-5 py-4 text-[14.5px] leading-relaxed text-slate-200">{m.text}</div>
                </div>
              )
            ))}
            {working && (
              <div className="flex items-start gap-3">
                <KrishnaAvatar size={36} />
                <div className="rounded-3xl rounded-tl-sm bg-white/5 border border-white/10 px-5 py-4 text-slate-300 italic text-[13px]">Reading the sky…</div>
              </div>
            )}
          </div>

          {!done && (
            <div className="relative flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-2">
              <input
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                data-testid="intake-input"
                placeholder={step?.placeholder || "Tell me…"}
                disabled={working}
                className="flex-1 bg-transparent text-[14px] text-slate-100 placeholder:text-slate-500 px-2 py-2"
              />
              {step?.id?.startsWith("event") && (
                <button data-testid="intake-skip" onClick={skip} disabled={working}
                        className="text-[11px] text-slate-400 hover:text-slate-200 px-2">
                  skip
                </button>
              )}
              <button data-testid="intake-send" onClick={submit} disabled={working || !input.trim()}
                      className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-all disabled:opacity-30"
                      style={{ background: "linear-gradient(140deg,#3B82F6,#7C3AED)" }}>
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </GlassSheet>
  );
}
